import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";

interface AuthUser {
  name: string;
  email: string;
}

interface AuthContextType {
  isInitialized: boolean;
  isLoggedIn: boolean;
  user: AuthUser | null;
  /** 登录态 accessToken（邮箱登录/注册成功后由服务端返回，供需登录态的接口使用） */
  token: string | null;
  login: (user?: AuthUser, token?: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem("auth");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed?.user) {
          setIsLoggedIn(true);
          setUser(parsed.user);
          setToken(parsed.token ?? null);
        }
      } catch {
        localStorage.removeItem("auth");
      }
    }
    setIsInitialized(true);
  }, []);

  const login = (userData?: AuthUser, accessToken?: string) => {
    setIsLoggedIn(true);
    if (userData) {
      setUser(userData);
      localStorage.setItem("auth", JSON.stringify({ user: userData, token: accessToken ?? null }));
    }
    if (accessToken) setToken(accessToken);
  };

  const logout = () => {
    setIsLoggedIn(false);
    setUser(null);
    setToken(null);
    localStorage.removeItem("auth");
  };

  return (
    <AuthContext.Provider value={{ isInitialized, isLoggedIn, user, token, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
